/**
 * MER-674: the Executive page's paging loop and availability probe, with no
 * Supabase in sight so node --test can drive them with a fake builder.
 *
 * execKpiData.ts is the thin TypeScript wrapper: it builds the scoped
 * PostgREST query (scopeTenant + order + range) and hands the builder
 * factory in here. Everything that decides how many requests go out, when
 * to stop, what "truncated" means and how a probe answer is classified is
 * in this file and pinned by scripts/exec-kpi-fetch.test.mjs.
 */

import { isMonthKey } from "./calendarMonth.mjs";
import { EXEC_KPI_PAGE_SIZE, pageBounds } from "./execKpi.mjs";

/** 64 pages x 1,000 rows. Eight times the contract's expected volume. */
export const MAX_PAGES = 64;

/**
 * A PostgREST answer that means "the relation is not there": PGRST205 is
 * the schema-cache miss, 42P01 is Postgres undefined_table, and the two
 * phrasings are what those carry as text. The wrapper widens this with
 * sanityGate's isColumnMissingError so both classifiers agree.
 */
export const TABLE_MISSING_RE = /PGRST205|42P01|could not find|does not exist/i;

export function isTableMissingMessage(message) {
  return typeof message === "string" && TABLE_MISSING_RE.test(message);
}

export class ExecKpiFetchError extends Error {
  constructor(message, tableMissing) {
    super(message);
    this.name = "ExecKpiFetchError";
    this.tableMissing = tableMissing === true;
  }
}

function errorMessage(error) {
  if (error && typeof error.message === "string" && error.message) return error.message;
  return "exec_kpi read failed";
}

/**
 * Read every page of a relation.
 *
 * @param {(from: number, to: number) => PromiseLike<{data: unknown[]|null, error: {message?: string}|null}>} queryPage
 *   Builds the query for one inclusive PostgREST range; awaiting it yields
 *   the page. Called once per page, in order.
 * @param {{ pageSize?: number, maxPages?: number, isMissing?: (m: string) => boolean }} [opts]
 * @returns {Promise<{ rows: unknown[], truncated: boolean, pages: number }>}
 *
 * Stops after the first short page. Exactly pageSize rows takes two
 * requests (the second comes back empty). If every page up to maxPages
 * came back full the result is flagged `truncated` so the page can say so;
 * it is never silently cut.
 */
export async function fetchAllPages(queryPage, opts = {}) {
  const pageSize = Math.max(1, Math.trunc(Number(opts.pageSize) || EXEC_KPI_PAGE_SIZE));
  const maxPages = Math.max(1, Math.trunc(Number(opts.maxPages) || MAX_PAGES));
  const isMissing = typeof opts.isMissing === "function" ? opts.isMissing : isTableMissingMessage;
  const rows = [];
  let pages = 0;
  for (let page = 0; page < maxPages; page += 1) {
    const { from, to } = pageBounds(page, pageSize);
    const { data, error } = await queryPage(from, to);
    pages += 1;
    if (error) {
      const message = errorMessage(error);
      throw new ExecKpiFetchError(message, isMissing(message));
    }
    const batch = Array.isArray(data) ? data : [];
    rows.push(...batch);
    if (batch.length < pageSize) return { rows, truncated: false, pages };
  }
  return { rows, truncated: true, pages };
}

/**
 * Classify a head-count answer.
 *
 *   missing    the relation is not there (see TABLE_MISSING_RE)
 *   empty      the relation exists and the tenant has zero rows
 *   available  at least one row
 *   error      any other failure
 */
export function classifyProbe(result, opts = {}) {
  const isMissing = typeof opts.isMissing === "function" ? opts.isMissing : isTableMissingMessage;
  if (!result || typeof result !== "object") return "error";
  if (result.error) {
    return isMissing(errorMessage(result.error)) ? "missing" : "error";
  }
  const count = Number(result.count);
  if (!Number.isFinite(count)) return "empty";
  return count > 0 ? "available" : "empty";
}

/**
 * Run a head-count probe and classify it. An unconfigured client is
 * "missing" without a request; a thrown error (abort timeout, network) is
 * "error", reported through opts.warn and never thrown at the caller.
 *
 * @param {() => PromiseLike<{count: number|null, error: {message?: string}|null}>} runProbe
 * @param {{ configured?: boolean, isMissing?: (m: string) => boolean, warn?: (m: string) => void }} [opts]
 */
export async function probeAvailability(runProbe, opts = {}) {
  const warn = typeof opts.warn === "function" ? opts.warn : () => {};
  if (opts.configured === false) return "missing";
  try {
    const result = await runProbe();
    const verdict = classifyProbe(result, opts);
    if (verdict === "error") warn(`probeExecKpi failed: ${errorMessage(result && result.error)}`);
    return verdict;
  } catch (err) {
    warn(`probeExecKpi failed: ${err instanceof Error ? err.message : String(err)}`);
    return "error";
  }
}

/**
 * MER-692: classify the one-row read of the workbook's latest period that
 * ExecKpiProvider makes after a probe says "available". The dashboard's
 * "Workbook month" chip is built from the answer.
 *
 * The read is `select workbook_latest_period, order by period desc, limit 1`,
 * scoped to the tenant. The ingest stamps every row of one workbook with the
 * same bare YYYY-MM key (the newest month on any sheet), so the newest row
 * carries it.
 *
 *   { period: 'YYYY-MM', problem: null }  a usable month
 *   { period: null, problem: <text> }     anything else: an error reply, no
 *                                         rows, or a value that is not a
 *                                         YYYY-MM month. The chip hides; the
 *                                         problem text goes to the console
 *                                         only, never onto the screen.
 */
export function classifyLatestPeriod(result) {
  if (!result || typeof result !== "object") {
    return { period: null, problem: "no reply" };
  }
  if (result.error) return { period: null, problem: errorMessage(result.error) };
  const rows = Array.isArray(result.data) ? result.data : [];
  if (rows.length === 0) return { period: null, problem: "no rows" };
  const raw = rows[0] && typeof rows[0] === "object" ? rows[0].workbook_latest_period : undefined;
  if (!isMonthKey(raw)) {
    return { period: null, problem: "workbook_latest_period is not a YYYY-MM month" };
  }
  return { period: raw, problem: null };
}

/**
 * Run the latest-period read and classify it. Resolves to the month or null;
 * never throws. Any null answer is reported through opts.warn so a hidden
 * chip is explainable from the console.
 *
 * @param {() => PromiseLike<{data: unknown[]|null, error: {message?: string}|null}>} runQuery
 * @param {{ warn?: (m: string) => void }} [opts]
 * @returns {Promise<string | null>}
 */
export async function readLatestPeriod(runQuery, opts = {}) {
  const warn = typeof opts.warn === "function" ? opts.warn : () => {};
  try {
    const { period, problem } = classifyLatestPeriod(await runQuery());
    if (period === null) warn(`exec_kpi latest period unavailable: ${problem}`);
    return period;
  } catch (err) {
    warn(`exec_kpi latest period unavailable: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}
